/** NFT Cost-Basis: Unknown ≠ 0 — nur bestätigte Null-Einstandswerte rechnen. */

export function normalizeNftClass(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

export function nftRowClass(row = {}) {
  return normalizeNftClass(
    row.classification ||
      row.type ||
      row.event ||
      row.taxType ||
      ""
  );
}

export function isNftSalePaymentRow(row = {}) {
  const cls = nftRowClass(row);
  return cls === "sale_payment" || cls === "sale";
}

export function isNftMarketplacePayoutReviewRow(row = {}) {
  const cls = nftRowClass(row);
  return (
    cls === "nft_marketplace_payout_review" ||
    row.requiresReceiptAllocation === true ||
    row.receiptScanRequired === true ||
    row.salePaymentAllocationStatus === "receipt_required"
  );
}

export function isNftTransformRow(row = {}) {
  const cls = nftRowClass(row);
  return (
    cls === "nft_transform" ||
    cls === "nft_transform_disposal" ||
    cls === "nft_transform_acquisition"
  );
}

export function isNftMintRow(row = {}) {
  const cls = nftRowClass(row);
  return cls === "nft_mint" || cls === "nft_galaxy_mint" || cls === "mint";
}

export function isNftAcquisitionPaymentRow(row = {}) {
  return isNftMintRow(row) || nftRowClass(row) === "nft_marketplace_purchase_payment";
}

function firstPresent(...values) {
  for (const v of values) {
    if (v === null || v === undefined || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function isNftKnownZeroCostBasis(row = {}) {
  const cost = firstPresent(
    row.nftAcquisitionCostFiat,
    row.nftAcquisitionCostEUR,
    row.acquisitionCostFiat,
    row.acquisitionCostEUR,
    row.purchaseValueFiat,
    row.purchaseValueEUR,
    row.grossBuyFiat,
    row.grossBuyEUR
  );

  const protocolCarryoverZero =
    nftRowClass(row) === "nft_transform_acquisition" &&
    row.noRealization === true &&
    row.basisCarryover === true &&
    row.costBasisUnknown !== true &&
    (
      row.costBasisSource === "germany_protocol_conversion_basis_carryover" ||
      row.costBasisSource === "italy_nft_to_nft_basis_carryover" ||
      row.costBasisSource === "nft_transform_migration_carryover"
    );

  return (
    cost === 0 &&
    (
      row.costBasisSource === "user_confirmed_zero_cost_basis" ||
      row.costBasisSource === "verified_free_mint" ||
      row.manualCostBasisOverride === true ||
      protocolCarryoverZero
    )
  );
}

export function hasNftKnownCostBasis(row = {}) {
  const cost = firstPresent(
    row.nftAcquisitionCostFiat,
    row.nftAcquisitionCostEUR,
    row.acquisitionCostFiat,
    row.acquisitionCostEUR,
    row.purchaseValueFiat,
    row.purchaseValueEUR,
    row.grossBuyFiat,
    row.grossBuyEUR
  );

  if (cost === null) return false;
  if (cost === 0) return isNftKnownZeroCostBasis(row);
  return true;
}

export function isNftCostBasisUnknown(row = {}) {
  const cls = nftRowClass(row);
  const isSale = cls === "sale_payment" || cls === "sale";
  const reviewReason = String(
    row.reviewReason || row.taxReviewReason || ""
  ).toUpperCase();

  /*
   * NFT-Transform:
   * Basisproblem und Bewertungsproblem strikt auseinanderhalten.
   */
  if (isNftTransformRow(row)) {
    const transformBasisReasons = new Set([
      "NFT_TRANSFORM_SOURCE_COST_BASIS_UNKNOWN",
      "NFT_TRANSFORM_SOURCE_BASIS_DEPENDS_ON_PRIOR_TRANSFORM_VALUATION",
      "NFT_TRANSFORM_SOURCE_ACQUISITIONS_INCOMPLETE",
      "NFT_TRANSFORM_SOURCE_ACQUISITION_DATE_UNKNOWN",
      "NFT_TRANSFORM_RESULT_ACQUISITION_BASIS_MISSING",
      "NFT_MIGRATION_SOURCE_BASIS_UNKNOWN",
    ]);

    if (transformBasisReasons.has(reviewReason)) {
      return true;
    }

    const transformValuationReasons = new Set([
      "NFT_TRANSFORM_TOTAL_VALUATION_MISSING",
      "NFT_TRANSFORM_TOTAL_FMV_MISSING",

      "NFT_TRANSFORM_RESULT_FMV_MISSING",
      "NFT_TRANSFORM_BOTH_SIDES_FMV_MISSING",

      "NFT_TRANSFORM_SOURCE_FMV_ALLOCATION_MISSING",
      "NFT_TRANSFORM_SOURCE_FMV_INVALID",

      "NFT_TRANSFORM_SOURCE_ALLOCATION_REQUIRED_MIXED_TAX_STATUS",
      "NFT_TRANSFORM_TERM_ALLOCATION_REQUIRED",

      "NFT_TRANSFORM_FMV_SIDES_MISMATCH",

      "NFT_TRANSFORM_OVERRIDE_CURRENCY_MISMATCH",
    ]);

    if (
      transformValuationReasons.has(reviewReason) ||
      reviewReason.includes("_FMV_") ||
      reviewReason.includes("VALUATION")
    ) {
      /*
       * Marktwert fehlt != historischer Einstand fehlt.
       */
      return false;
    }
  }

  const explicitUnknown = Boolean(
    row.missingCostBasis === true ||
      row.costBasisUnknown === true ||
      row.taxComputationSkipped === true ||
      row.isTaxComputationBlocked === true ||
      row.requiresUserCostBasis === true ||
      row.userCostBasisRequired === true ||
      row.needsReview === true ||
      row.pricingStatus === "missing_cost_basis" ||
      row.displayPriceStatus === "COST_BASIS_NOT_AVAILABLE" ||
      row.displayPriceStatus === "COST_BASIS_UNKNOWN" ||
      row.taxResolutionStatus === "needs_cost_basis_review" ||
      row.taxStatus === "review_required" ||
      row.taxableStatus === "unknown_cost_basis" ||
      row.acquisitionStatus === "transfer_in_unknown_basis" ||
      row.acquisitionStatus === "raw_mint_no_payment" ||
      row.acquisitionStatus === "no_prior_acquisition" ||
      row.acquisitionStatus === "internal_nft_transfer"
  );

  if (explicitUnknown) {
    return true;
  }

  /*
   * Sale-Fallback:
   * Ohne FIFO / bekannte AK darf Sale niemals als 0-Einstand durchlaufen.
   */
  if (isSale) {
    const hasFifoLegs = Array.isArray(row.fifoLegs) && row.fifoLegs.length > 0;
    const hasKnownBasis = hasNftKnownCostBasis(row);

    if (!hasFifoLegs && !hasKnownBasis) {
      return true;
    }
  }

  return false;
}

export function nftPurchaseDisplayValue(row = {}) {
  if (isNftCostBasisUnknown(row) && !isNftKnownZeroCostBasis(row)) {
    return null;
  }

  return firstPresent(
    row.grossBuyFiat,
    row.grossBuyEUR,
    row.nftAcquisitionCostFiat,
    row.nftAcquisitionCostEUR,
    row.acquisitionCostFiat,
    row.acquisitionCostEUR
  );
}

export function nftProfitDisplayValue(row = {}) {
  if (isNftCostBasisUnknown(row) && !isNftKnownZeroCostBasis(row)) {
    return null;
  }

  return firstPresent(
    row.taxableProfitFiat,
    row.taxableProfitEUR,
    row.taxableProfit,
    row.grossProfitFiat,
    row.grossProfitEUR
  );
}
